/**
 * The decision behind `FitText`: is this text cut off, and if so, how does
 * a person reach the rest of it?
 *
 * DOM-free so the rule is testable in plain Node (test/fitText.test.ts).
 * The component measures; this module decides.
 */

/** What `FitText` reads off its element after layout. */
export interface FitMeasure {
  scrollWidth: number;
  clientWidth: number;
  scrollHeight: number;
  clientHeight: number;
}

/** How many lines the text may take: 1 truncates with an ellipsis, more clamps. */
export type FitLines = 1 | 2 | 3 | 4 | 5 | 6;

/**
 * The Tailwind class for each line count, spelt out in full so Tailwind's
 * source scan sees every one (a `line-clamp-${n}` template generates nothing).
 */
export const FIT_CLASS: Record<FitLines, string> = {
  1: 'truncate',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
};

/**
 * Sub-pixel layout can leave a scroll size a pixel above its client size
 * with nothing hidden (a descender, a rounding step); only more than this
 * counts as cut.
 */
const SLACK_PX = 1;

/**
 * True when the element is hiding some of its text. One line overflows
 * sideways (`truncate`); a clamp overflows downwards (`line-clamp-*`), and
 * an unbreakable word in a clamp can overflow sideways too, so a clamp
 * checks both.
 */
export function isCut(m: FitMeasure, lines: FitLines): boolean {
  const wide = m.scrollWidth - m.clientWidth > SLACK_PX;
  if (lines === 1) return wide;
  return wide || m.scrollHeight - m.clientHeight > SLACK_PX;
}

/**
 * What FitText renders:
 * - `plain` — the whole text shows, so no tooltip (one that repeats the
 *   visible words is noise);
 * - `tooltip` — the text is cut, or the visible words are a shortened form of
 *   a longer `text`, and the element is its own tab stop so a keyboard can
 *   reach the rest;
 * - `tooltip-in-control` — the same, but the text sits inside a button, link
 *   or row that already takes focus: the tooltip opens when that control is
 *   focused from the keyboard, and the text adds no second tab stop.
 */
export type FitMode = 'plain' | 'tooltip' | 'tooltip-in-control';

export function fitMode(input: { cut: boolean; shortened: boolean; insideControl: boolean }): FitMode {
  if (!input.cut && !input.shortened) return 'plain';
  return input.insideControl ? 'tooltip-in-control' : 'tooltip';
}

/**
 * The full words for the tooltip and for screen readers: an explicit `text`
 * wins (the caller shortened what shows), otherwise the children when they
 * are plain words, otherwise what the element rendered.
 */
export function fullText(explicit: string | undefined, childText: string | undefined, rendered: string | undefined): string {
  const pick = explicit ?? childText ?? rendered ?? '';
  return pick.trim();
}

/** Whether a caller-supplied full text says more than what shows. */
export function isShortened(explicit: string | undefined, shown: string | undefined): boolean {
  if (explicit === undefined) return false;
  return explicit.trim() !== (shown ?? '').trim();
}

/**
 * The focusable ancestors FitText defers to: when the text sits inside one,
 * keyboard focus on that control opens the tooltip instead of the text
 * taking a tab stop of its own (a focusable span inside a button is invalid
 * and confusing).
 */
export const CONTROL_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
  '[role="button"]',
  '[role="link"]',
  '[role="option"]',
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="menuitemcheckbox"]',
  '[role="row"]',
  '[role="gridcell"]',
  '[role="treeitem"]',
  '[role="tab"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
].join(',');
